import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from './AuthGuard';
import { useFilterPersistence } from '../hooks/useFilterPersistence';
import {
  LayoutDashboard,
  Server,
  ShieldCheck,
  BarChart3,
  Share2,
  LogOut,
  Zap,
} from 'lucide-react';
import { cn } from '../lib/utils';

const navItems = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/inventory', label: 'Inventario', icon: Server },
  { to: '/security', label: 'Seguridad', icon: ShieldCheck },
  { to: '/metrics', label: 'Monitoreo', icon: BarChart3 },
  { to: '/relationships', label: 'Relaciones', icon: Share2 },
];

export function NavigationShell() {
  const { logout } = useAuth();
  useFilterPersistence();

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar */}
      <aside className="flex w-64 flex-col bg-gray-900 relative">
        {/* Subtle gradient overlay for depth */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950 pointer-events-none" />

        {/* Logo / Brand */}
        <div className="relative flex h-16 items-center gap-3 border-b border-white/5 px-6">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-primary-400 to-primary-600 shadow-lg shadow-primary-500/25">
            <Zap className="h-4 w-4 text-white" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-bold text-white tracking-tight">NetControl</span>
            <span className="text-[10px] text-gray-500">Control Plane SRE</span>
          </div>
        </div>

        {/* Navigation links */}
        <nav className="relative flex-1 space-y-1 px-3 py-5">
          <p className="mb-3 px-3 text-[10px] font-semibold uppercase tracking-widest text-gray-600">
            Navegación
          </p>
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                cn(
                  'group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200',
                  isActive
                    ? 'bg-white/10 text-white'
                    : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
                )
              }
            >
              {({ isActive }) => (
                <>
                  {/* Active indicator bar */}
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r-full bg-primary-400 shadow-sm shadow-primary-400/50" />
                  )}
                  <item.icon className={cn(
                    'h-4 w-4 shrink-0 transition-colors duration-200',
                    isActive ? 'text-primary-400' : 'text-gray-500 group-hover:text-gray-300'
                  )} />
                  <span>{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="relative border-t border-white/5 p-3">
          <button
            onClick={logout}
            className="group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-500 transition-all duration-200 hover:bg-red-500/10 hover:text-red-400"
            aria-label="Cerrar sesión"
          >
            <LogOut className="h-4 w-4 shrink-0 transition-colors group-hover:text-red-400" />
            Cerrar sesión
          </button>
          <p className="mt-3 px-3 text-[10px] text-gray-600">v1.0 • Network Control Plane</p>
        </div>
      </aside>

      {/* Main content - rosado muy sutil de fondo */}
      <main className="flex-1 overflow-y-auto bg-[#fdf8fa] p-6">
        <Outlet />
      </main>
    </div>
  );
}
